<template>
    <v-card
        class="hotel-card"
        :class="{'cursor-pointer': compareMode}"
        variant="outlined"
        :hover="compareMode"
        @[clickEvent]="selectToCompare(offer)"
    >
        <div class="d-flex wrapper">
            <div v-if="compareMode && !isAllSoldOut" class="d-flex align-center flex-shrink-0">
                <v-checkbox
                    v-model="selectAllOffers"
                    density="compact"
                    :ripple="false"
                    hide-details
                    @update:model-value="toggleAllOffers"
                />
            </div>
            <div style="position: relative">
                <v-img
                    tile
                    :src="$filters.imageUrl(offer.thumb, 350)"
                    :alt="offer.name"
                    eager
                    width="200"
                    max-width="200"
                    height="200"
                    :lazy-src="$filters.srcHost('/placeholder.png')"
                    cover
                    @error="onImgError"
                >
                    <template v-slot:placeholder>
                        <v-row
                            v-show="!imgError && offer.thumb"
                            class="fill-height ma-0"
                            align="center"
                            justify="center"
                        >
                            <v-progress-circular indeterminate color="grey-lighten-5" />
                        </v-row>
                    </template>
                </v-img>
                <wishlist-like
                    :info="favoritesInfo"
                    :query="{
                        supplierCode: offer.supplierCode,
                        hotelCode: offer.hotelCode,
                        cityCode: offer.cityCode,
                        productType: 'hotel',
                    }"
                    :person="person"
                    :product-runtime-store="productRuntimeStore"
                    :in-wishlist="offer.wishlist"
                />
                <card-availability-badge :offer="offer" />
            </div>
            <div
                class="hotel-card__content d-flex flex-column ma-2 flex-grow-1 flex-shrink-1 text-truncate"
                style="position: relative"
            >
                <div class="d-flex align-center flex-wrap">
                    <hotel-stars :hotel="offer" />
                    <hotel-type-label class="ms-1" :offer="offer" />
                    <hotel-own-label class="ms-1" :offer="offer" />
                    <v-icon size="x-small" start class="ml-2 cursor-pointer" @click="showHotelInfoModal">
                        mdi-information-outline
                    </v-icon>
                </div>
                <div class="hotel-card__content-title font-weight-medium text-truncate">
                    <a
                        v-if="($route.name === 'hotels' && infoInNewTab) || isTour"
                        :href="$router.resolve(hotelPageLink).href"
                        target="_blank"
                        :title="offer.name"
                        class="text-decoration-none"
                    >
                        {{ offer.name }}
                    </a>
                    <nuxt-link
                        v-else-if="$route.name === 'hotels' || $route.name === 'favorites'"
                        no-prefetch
                        :title="offer.name"
                        :to="hotelPageLink"
                        class="text-decoration-none"
                    >
                        {{ offer.name }}
                    </nuxt-link>
                    <span v-else :title="offer.name">
                        {{ offer.name }}
                    </span>
                    <supplier-name-label v-if="isTO1" :supplier-name="supplierNameText" />
                    <div v-if="isAddOn" class="d-flex align-center text-caption">
                        <v-icon :title="$t('date')" start size="small">mdi-calendar</v-icon>
                        <span>
                            {{ $filters.dateFormat(offer.startDate) }} - {{ $filters.dateFormat(offer.endDate) }}
                        </span>
                    </div>
                    <v-tooltip v-if="isShowDynamicRate" :text="$t('dynamic_rate')" location="bottom">
                        <template v-slot:activator="{props}">
                            <v-icon class="ms-1" v-bind="props" color="primary"> mdi-sync </v-icon>
                        </template>
                        <span>{{ $t('dynamic_rate') }}</span>
                    </v-tooltip>
                </div>
                <div class="hotel-card__content-info d-flex flex-column">
                    <hotel-location-label :offer="offer" />
                    <hotel-address-label
                        :address="offer.address"
                        :offer="offer"
                        :hide-show-on-map-button="hideShowOnMapButton"
                    />
                    <hotel-services
                        v-if="offer && offer.services && offer.services.length && isServicesOnTop"
                        :offer="offer"
                        class="mt-2"
                    />
                    <div class="mt-auto" />
                    <hotel-services
                        v-if="offer && offer.services && offer.services.length && !isServicesOnTop"
                        :offer="offer"
                        class="mt-2"
                    />
                </div>
                <!--v-chip
                    v-for="(classification, classificationIndex) in offer.classifications"
                    :key="classificationIndex"
                    x-small
                    class="ma-1"
                    v-text="classification"
                /-->
                <tripadvisor-rating-wrapper
                    v-if="!hideTripadvisorRating"
                    :tripadvisor-rating="offer.tripadvisorRating"
                />
                <template v-if="showRating">
                    <v-divider class="my-2" />
                    <supplier-rating :rating-object="offer.supplierRating" />
                </template>
                <template v-if="isShowRatingByClientsFeedback">
                    <v-divider class="my-2" />
                    <rating-clients-feedback
                        :rating-object="offer.ratingByClientsFeedback"
                        :product-code="offer.hotelCode"
                        :city-code="offer.cityCode"
                        :supplier-code="offer.supplierCode"
                    />
                </template>
                <slot :min-price-room-offer="minPriceRoomOffer" :hotel-page-link="hotelPageLink" />
                <v-img
                    v-if="offer.recommended && showPreferred"
                    :src="$filters.srcHost('/preferred.png')"
                    :width="120"
                    class="preferred-thumb"
                />
                <v-img
                    v-if="offer.tariffRecommended && showPreferred && isFavoritesPage"
                    :src="$filters.srcHost('/long-stay.png')"
                    :width="120"
                    class="long-stay-fav"
                />
            </div>
            <div v-if="!hideRoomOffers" class="ma-4 price-col flex-shrink-0 text-right" style="position: relative">
                <slot
                    v-if="!(isMultiRoom && isPackage)"
                    name="price"
                    :room="minPriceRoomOffer"
                    :offer="minPriceRoomOffer"
                    :offer-key="minPriceRoomOffer.groupedOffers && minPriceRoomOffer.groupedOffers[0].offerKey"
                >
                    <div style="position: relative">
                        <hotel-offer-price-block
                            v-if="!isAllSoldOut"
                            class="hotel-card__price-block"
                            :offer="offer"
                            :search-request="searchRequest"
                            :hotel-page-link="hotelPageLink"
                        />
                        <sold-out-modal v-else />
                    </div>
                </slot>
                <div
                    class="hotel-card__view-offers text-primary cursor-pointer text-no-wrap"
                    data-cy="hotel-card-toggle-offers"
                    @click="expanded = !expanded"
                >
                    <span class="show-rooms-label">
                        {{ expanded ? $t('hide_room_offers') : $t('view_room_offers') }}
                    </span>
                    <span class="show-rooms-label__short">
                        {{ expanded ? $t('hide_room_offers__short') : $t('view_room_offers__short') }}
                    </span>
                    <v-icon color="primary">
                        {{ expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                    </v-icon>
                </div>
                <v-img
                    v-if="offer.tariffRecommended && showPreferred"
                    :src="$filters.srcHost('/long-stay.png')"
                    class="long-stay"
                />
            </div>
            <recommended-icon v-if="offer.recommended && !showPreferred" class="recommended-thumb" />
        </div>
        <v-expand-transition>
            <div v-if="expanded">
                <hotel-room-offers-wrapper
                    v-model="selectedOfferKeys"
                    :offer="offer"
                    :compare-mode="compareMode"
                    :search-request="searchRequest"
                    :is-package="isPackage"
                >
                    <template v-slot:price="{room}">
                        <slot name="price" :room="room" :offer="room" :offer-key="room.groupedOffers[0].offerKey" />
                    </template>
                </hotel-room-offers-wrapper>
                <slot v-if="isMultiRoom" name="multiRoomSelect">
                    <hotel-multiple-room-select-block
                        :search-request="searchRequest"
                        :offer="offer"
                        :selected-offer-keys="selectedOfferKeys"
                        class="pa-4 pt-0"
                    />
                </slot>
            </div>
        </v-expand-transition>
    </v-card>
</template>

<script lang="ts">
    import {Component, Emit, mixins, Prop, Watch} from 'vue-facing-decorator'
    import HotelServices from '~src/components/hotels/snippets/hotelServices.src'
    import TripadvisorRatingWrapper from '~src/components/hotels/snippets/tripadvisorRatingWrapper.src'
    import SupplierRating from '@/components/hotels/snippets/SupplierRating.vue'
    import HotelRoomOffers from '~/components/search/offers/HotelRoomOffers.vue'
    import DiscountLabel from '~src/components/snippets/discountLabel.src'
    import CommissionAmount from '~/components/snippets/CommissionAmount.vue'
    import HotelRoomOffersWrapper from '~src/components/search/offers/HotelRoomOffersWrapper.vue'
    import HotelOfferPriceBlock from '~src/components/search/offers/hotelOfferPriceBlock.src'
    import HotelNameAndRating from '~src/components/hotels/snippets/hotelNameAndRating.src'
    import HotelPriceNote from '@/components/hotels/snippets/HotelPriceNote.vue'
    import HotelMultipleRoomSelectBlock from '~src/components/hotels/snippets/hotelMultipleRoomSelectBlock.src'
    import HotelOwnLabel from '~src/components/hotels/snippets/HotelOwnLabel.vue'
    import HotelStars from '~src/components/hotels/snippets/hotelStars.src'
    import HotelTypeLabel from '@/components/hotels/snippets/HotelTypeLabel.vue'
    import HotelLocationLabel from '~src/components/hotels/snippets/hotelLocationLabel.src'
    import HotelAddressLabel from '~src/components/hotels/snippets/hotelAddressLabel.src'
    import RecommendedIcon from '@/components/search/RecommendedIcon.vue'
    import WishlistLike from '@/components/search/offers/WishlistLike.vue'
    import SupplierNameLabel from '@/components/hotels/snippets/SupplierNameLabel.vue'
    import SoldOutModal from '@/components/snippets/SoldOutModal.vue'
    import {AuthInfoMixin} from '@/mixins/authInfoMixin.src'
    import CardAvailabilityBadge from '~src/components/search/offers/cardAvailabilityBadge.src'
    import RatingClientsFeedback from '@/components/hotels/snippets/RatingClientsFeedback.vue'
    import {SHOW_HOTEL_INFO_MODAL} from '~/utils/event-bus'

    @Component({
        components: {
            RatingClientsFeedback,
            WishlistLike,
            RecommendedIcon,
            HotelOwnLabel,
            HotelMultipleRoomSelectBlock,
            HotelPriceNote,
            HotelNameAndRating,
            DiscountLabel,
            HotelServices,
            TripadvisorRatingWrapper,
            HotelRoomOffers,
            CommissionAmount,
            HotelOfferPriceBlock,
            HotelRoomOffersWrapper,
            HotelStars,
            HotelTypeLabel,
            HotelLocationLabel,
            HotelAddressLabel,
            SupplierNameLabel,
            SoldOutModal,
            SupplierRating,
            CardAvailabilityBadge,
        },
    })
    export class HotelOfferCard extends mixins(AuthInfoMixin) {
        @Prop({default: false, type: Boolean}) compareMode
        @Prop({default: () => ({})}) offer
        @Prop({required: true}) searchRequest
        @Prop({default: false, type: Boolean}) hideRoomOffers
        @Prop({default: false, type: Boolean}) hideShowOnMapButton
        @Prop({default: false, type: Boolean}) hideTripadvisorRating
        @Prop({default: false, type: Boolean}) showSupplierRating
        @Prop({default: false, type: Boolean}) isPackage
        @Prop({default: false, type: Boolean}) isTour
        @Prop({default: () => [], type: Array}) initialSelectedOfferKeys
        @Prop({default: false, type: Boolean}) isAddOn

        expanded = false
        imgError = false
        selectedOfferKeys = []

        selectedOfferKeysInitiated = false
        selectAllOffers = false

        @Emit()
        changeSelectedOfferKeys() {
            return this.selectedOfferKeys
        }

        @Watch('selectedOfferKeys', {immediate: false})
        onChangeSelectedOfferKeys() {
            if (this.selectedOfferKeysInitiated) {
                this.changeSelectedOfferKeys()
            }
        }

        @Watch('compareOfferKeys', {immediate: false})
        onChangeCompareOfferKeys() {
            this.selectAllOffers = this.isKeysTheSame
        }

        toggleAllOffers() {
            if (this.selectAllOffers) {
                hotelsStore.ADD_ROOM_TO_COMPARE({hotelCode: this.offer.hotelCode, rooms: this.availableRoomKeys})
            } else {
                hotelsStore.ADD_ROOM_TO_COMPARE({hotelCode: this.offer.hotelCode})
            }
        }

        showHotelInfoModal() {
            EventBus.$emit(SHOW_HOTEL_INFO_MODAL, {
                hotelName: this.offer.name,
                hotelId: null,
                hotelStars: this.offer?.category,
                mainImage: this.offer?.thumb,
                request: {
                    supplierCode: this.offer.supplierCode,
                    cityCode: this.offer.cityCode,
                    hotelCode: this.offer.hotelCode,
                },
            })
        }

        async mounted() {
            this.selectedOfferKeys = [...this.initialSelectedOfferKeys]
            await this.$nextTick()
            this.selectedOfferKeysInitiated = true
            if (this.isKeysTheSame) this.selectAllOffers = true
        }

        get compareOfferKeys() {
            return hotelsStore.offersToCompare.find(hotel => hotel.hotelCode === this.offer.hotelCode)?.rooms || []
        }

        get availableRoomKeys() {
            return this.offer.rooms.filter(({soldOut}) => !soldOut).map(room => room.groupedOffers[0].offerKey)
        }

        get isKeysTheSame() {
            if (this.availableRoomKeys.length !== this.compareOfferKeys.length || !this.compareOfferKeys.length) {
                return false
            }
            const set1 = new Set(this.availableRoomKeys)
            const set2 = new Set(this.compareOfferKeys)
            return set1.size === set2.size && [...set1].every(value => set2.has(value))
        }

        onImgError() {
            this.imgError = true
            return false
        }

        selectToCompare(offer) {
            if (this.compareMode) {
                hotelsStore.SELECT_TO_COMPARE(offer.hotelCode)
            }
        }

        get isShowDynamicRate() {
            return this.offer.rooms.some(room => room.packageOnly === true)
        }

        get isFavoritesPage() {
            return this.$route.name === 'favorites'
        }

        get isAllSoldOut() {
            return this.offer.rooms.every(room => room.soldOut)
        }

        get isMultipleSuppliers() {
            return this.offer.rooms?.some(room => room.hotelSupplierName !== this.offer.supplierName)
        }

        get supplierNameText() {
            if (!this.isMultipleSuppliers) {
                return this.offer.supplierName
            } else {
                return hotelsStore.filters.supplierName?.length === 1
                    ? hotelsStore.filters.supplierName[0]
                    : this.$t('multiple_suppliers')
            }
        }

        get hotelPageLink() {
            return hotelsRuntimeStore.hotelPageLink(this.offer, this.searchRequest, this.isTour)
        }

        get minPriceRoomOffer() {
            if (!this.offer.rooms) return {}

            return hotelsRuntimeStore.minPriceRoomOffer(this.offer)
        }

        get infoInNewTab() {
            return this.$conf.public.infoInNewTab
        }

        get isMultiRoom() {
            return this.searchRequest.rooms?.length > 1
        }

        get person() {
            return authStore.person
        }

        get productRuntimeStore() {
            return hotelsRuntimeStore
        }

        get isTO1() {
            return authStore.isTO1
        }

        get favoritesInfo() {
            return {
                name: this.offer.name,
                thumb: this.offer.thumb,
                address: this.offer.address,
                distance: this.offer.distance,
                district: this.offer.district,
                services: this.offer.services,
                otaCodeServices: this.offer.otaCodeServices,
                category: this.offer.category,
                type: this.offer.type,
                searchRequest: this.searchRequest,
                recommended: this.offer.recommended,
                tariffRecommended: this.offer.tariffRecommended,
            }
        }

        get showPreferred() {
            return false
        }

        get showRating() {
            return this.showSupplierRating && this.offer?.supplierRating
        }

        get clickEvent() {
            return this.compareMode ? 'click.native' : null
        }

        get isShowRatingByClientsFeedback() {
            return this.$conf.public.account.reviewsAndFeedback && this.offer?.ratingByClientsFeedback
        }

        get isServicesOnTop() {
            return false
        }
    }

    export default HotelOfferCard
</script>

<style scoped lang="scss">
    .wrapper {
        min-height: 200px;
    }

    .price-col {
        width: 180px;

        .show-rooms-label__short {
            display: none;
        }
    }

    .hotel-card {
        overflow: hidden;
        cursor: unset;
    }

    .recommended-thumb {
        position: absolute;
        top: 0;
        left: 0;
        width: 40px;
        height: 40px;
    }

    .preferred-thumb {
        position: absolute;
        bottom: 0;
        left: 0;
    }

    .long-stay {
        position: absolute;
        bottom: -8px;
        left: 0;
        right: 0;
        margin-left: auto;
        margin-right: auto;
    }

    .long-stay-fav {
        position: absolute;
        bottom: 0;
        right: 0;
    }

    //TODO Remove after layout redesign
    @media (max-width: 1440px) {
        .price-col {
            width: 130px;

            .show-rooms-label {
                display: none;

                &__short {
                    display: inline;
                }
            }
        }
    }
</style>
